import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ChevronLeft, FileText, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { EmailComposer } from "@/components/t4r/email/EmailComposer";

const TemplateList = ({ templates, onEdit, onNew, onDelete }) => (
  <div className="space-y-2" data-testid="template-list">
    <Button size="sm" className="gap-1 bg-green-700 text-white hover:bg-green-800" onClick={onNew} data-testid="template-new"><Plus size={12} /> New template</Button>
    {templates === null && <div className="flex justify-center py-6"><Loader2 className="animate-spin text-slate-400" size={18} /></div>}
    {templates?.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">No templates yet.</p>}
    {(templates || []).map((t) => (
      <div key={t.id} className="flex items-center justify-between gap-2 rounded-lg border border-border px-3 py-2.5" data-testid={`template-${t.id}`}>
        <button onClick={() => onEdit(t)} className="flex min-w-0 items-center gap-2 text-left">
          <FileText size={14} className="shrink-0 text-green-700" />
          <span className="min-w-0"><span className="block truncate text-sm font-medium">{t.name}</span><span className="block truncate text-xs text-muted-foreground">{t.subject}</span></span>
        </button>
        <div className="flex shrink-0 gap-1">
          <button onClick={() => onEdit(t)} className="rounded p-1.5 text-slate-500 hover:bg-slate-100" title="Edit" data-testid={`template-edit-${t.id}`}><Pencil size={13} /></button>
          <button onClick={() => onDelete(t)} className="rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600" title="Delete" data-testid={`template-delete-${t.id}`}><Trash2 size={13} /></button>
        </div>
      </div>
    ))}
  </div>
);

export const EmailTemplatesDialog = ({ open, onClose }) => {
  const [templates, setTemplates] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/lms/admin/email-templates").then(({ data }) => setTemplates(data.templates)).catch(() => toast.error("Couldn't load templates")), []);
  useEffect(() => { if (open) { setTemplates(null); setEditing(null); load(); } }, [open, load]);
  const remove = async (t) => {
    if (!window.confirm(`Delete the template "${t.name}"?`)) return;
    try { await api.delete(`/lms/admin/email-templates/${t.id}`); toast.success("Template deleted"); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={`${editing ? "max-w-[1280px]" : "max-w-xl"} max-h-[92vh] overflow-y-auto grid-cols-[minmax(0,1fr)]`} data-testid="email-templates-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? (editing.id ? `Edit "${editing.name}"` : "New email template") : "Email templates"}</DialogTitle>
          <DialogDescription className="text-xs">Reusable T4R group emails. Pick one when sending to a group, then tweak it before it goes.</DialogDescription>
        </DialogHeader>
        {editing
          ? <div className="space-y-2">
              <button onClick={() => setEditing(null)} className="inline-flex items-center gap-1 text-xs font-medium text-green-800 hover:underline" data-testid="template-back"><ChevronLeft size={12} /> All templates</button>
              <EmailComposer key={editing.id || "new"} mode="template" template={editing.id ? editing : null} onSaved={() => { setEditing(null); load(); }} />
            </div>
          : <TemplateList templates={templates} onEdit={setEditing} onNew={() => setEditing({})} onDelete={remove} />}
      </DialogContent>
    </Dialog>
  );
};
